import React from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { BucketData, Comparison } from "interfaces/marketplace-dashboard";
import dayjs from "dayjs";

interface TrendChartProps {
  title: string;
  series: BucketData[];
  comparison: Comparison;
  formatValue?: (v: number) => string;
  height?: number;
}

const comparisonLabel: Record<Comparison, string> = {
  wow: "Prev Week",
  mom: "Prev Month",
  qoq: "Prev Quarter",
  yoy: "Prev Year",
  none: "",
};

const TrendChart: React.FC<TrendChartProps> = ({
  title,
  series,
  comparison,
  formatValue = (v) => v.toLocaleString(),
  height = 220,
}) => {
  if (!series || series.length === 0) {
    return null;
  }

  const chartType: "bar" | "line" = series.length === 1 ? "bar" : "line";

  const categories = series.map((b) => dayjs(b.periodStart).format("MMM D"));

  const currentData = series.map((b) => b.value ?? 0);
  const hasBaseline = comparison !== "none" && series.some((b) => b.baselineValue !== null);
  const baselineData = series.map((b) => b.baselineValue ?? 0);

  const chartSeries: ApexAxisChartSeries = [
    { name: "Current", data: currentData },
    ...(hasBaseline
      ? [{ name: comparisonLabel[comparison] || "Baseline", data: baselineData }]
      : []),
  ];

  const options: ApexOptions = {
    chart: {
      type: chartType,
      toolbar: { show: false },
      sparkline: { enabled: false },
      animations: { enabled: true, speed: 400 },
    },
    stroke: {
      curve: "smooth",
      width: chartType === "line" ? [2, 2] : [0],
      dashArray: hasBaseline ? [0, 4] : [0],
    },
    colors: ["#FFAC39", "#BDBDBD"],
    xaxis: {
      categories,
      labels: {
        style: { fontSize: "11px", colors: "#888" },
        rotate: -30,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        formatter: (val: number) => formatValue(val),
        style: { fontSize: "11px", colors: "#888" },
      },
    },
    tooltip: {
      y: { formatter: (val: number) => formatValue(val) },
    },
    grid: {
      borderColor: "#f0f0f0",
      strokeDashArray: 4,
    },
    legend: {
      show: hasBaseline,
      position: "top",
      horizontalAlign: "right",
      fontSize: "12px",
    },
    dataLabels: { enabled: false },
    title: {
      text: title,
      align: "left",
      style: { fontSize: "13px", fontWeight: "600", color: "#1d2023" },
    },
  };

  return (
    <ReactApexChart
      options={options}
      series={chartSeries}
      type={chartType}
      height={height}
    />
  );
};

export default TrendChart;
